// Shared state for Pixie's surface: the Recipe | Pixie switch, the Workspace, the
// first-time tooltip, the resume toast and the Android portrait lock. See
// docs/discovery/player-ai-chat.md.
import { shopWindowTypes, valueNames } from "@/modules/Constants";
import { isIOS } from "@/modules/orientationManager";
import type ApplicationData from "@/modules/ApplicationData";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type ShopWindowConfigList from "@/plugin/ShopWindowConfigList";

// Vendor-prefixed fullscreen API (Safari).
type WebkitFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

// ScreenOrientation.lock is missing from the TS DOM lib.
type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: string) => Promise<void>;
};

type ToastListener = (text: string) => void;

const TIP_SEEN_KEY = "pixmoto_pixie_tip_seen";
let _tipSeen: boolean | null = null;
let _toastListeners: ToastListener[] = [];
let _pendingToast: { text: string; at: number } | null = null;
// A toast raised just before a chain advance remounts the player still shows.
const PENDING_TOAST_MS = 4000;
let _portraitLocked = false;

export function findAskConfig(
  applicationData: ApplicationData,
): ShopWindowConfig | null {
  const configs = applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG_LIST,
  ) as ShopWindowConfigList | undefined;
  if (!configs) {
    return null;
  }
  for (let n = 0, max = configs.count; n < max; n++) {
    const config = configs.atIndex(n);
    if (config.type === shopWindowTypes.ASK) {
      return config;
    }
  }
  return null;
}

export function isPixieOpen(applicationData: ApplicationData): boolean {
  const config = applicationData.getValue(valueNames.SHOP_WINDOW_CONFIG) as
    | ShopWindowConfig
    | undefined;
  return !!config && config.type === shopWindowTypes.ASK;
}

export function formatClock(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

// localStorage can throw (blocked / partitioned storage); the tip then shows
// once per player load instead of once per device.
export function hasSeenPixieTip(): boolean {
  if (_tipSeen === null) {
    try {
      _tipSeen = window.localStorage.getItem(TIP_SEEN_KEY) === "1";
    } catch {
      _tipSeen = false;
    }
  }
  return _tipSeen;
}

export function markPixieTipSeen() {
  _tipSeen = true;
  try {
    window.localStorage.setItem(TIP_SEEN_KEY, "1");
  } catch {
    /* storage unavailable: remembered for this load only */
  }
}

export function showPixieToast(text: string) {
  _pendingToast = { text: text, at: Date.now() };
  for (let i = 0; i < _toastListeners.length; i++) {
    _toastListeners[i](text);
  }
}

// The toast not yet on screen, once; null when none or too old.
export function takePendingToast(): string | null {
  const pending = _pendingToast;
  _pendingToast = null;
  if (!pending || Date.now() - pending.at > PENDING_TOAST_MS) {
    return null;
  }
  return pending.text;
}

export function subscribePixieToast(listener: ToastListener): () => void {
  _toastListeners.push(listener);
  return function () {
    _toastListeners = _toastListeners.filter(function (l) {
      return l !== listener;
    });
  };
}

// Android only: rotating a phone to portrait for Pixie needs the Screen
// Orientation lock, which Chrome grants only while the document is fullscreen
// (immersive landscape auto-enters it). iOS Safari has no lock, so Pixie opens
// laid out for the landscape viewport instead. Resolves true when locked.
export function lockPortraitForPixie(): Promise<boolean> {
  const orientation: LockableOrientation | null =
    typeof screen !== "undefined" ? screen.orientation : null;
  const fsEl =
    typeof document !== "undefined" &&
    (document.fullscreenElement ||
      (document as WebkitFullscreenDocument).webkitFullscreenElement);
  if (
    isIOS() ||
    !fsEl ||
    !orientation ||
    typeof orientation.lock !== "function"
  ) {
    return Promise.resolve(false);
  }
  _portraitLocked = true;
  watchFullscreenExit();
  try {
    return orientation.lock("portrait").then(
      function () {
        return true;
      },
      function () {
        _portraitLocked = false;
        return false;
      },
    );
  } catch {
    _portraitLocked = false;
    return Promise.resolve(false);
  }
}

// The lock ends with fullscreen (an OS swipe, the fullscreen button), so the
// flag must too. The listeners are added once per page and never removed on
// purpose: the lock is page-wide state, and the guard keeps it to one pair.
let _watchingFullscreen = false;
function watchFullscreenExit() {
  if (_watchingFullscreen) {
    return;
  }
  _watchingFullscreen = true;
  const onChange = function () {
    if (
      !document.fullscreenElement &&
      !(document as WebkitFullscreenDocument).webkitFullscreenElement
    ) {
      _portraitLocked = false;
    }
  };
  document.addEventListener("fullscreenchange", onChange);
  document.addEventListener("webkitfullscreenchange", onChange);
}

// True while Pixie holds the portrait lock, so the rotate-to-portrait handler
// keeps the fullscreen the lock depends on (Experience._onOrientationChangeFs).
export function isPixiePortraitLocked(): boolean {
  return _portraitLocked;
}

// Releases the lock without rotating back: the viewer turns the phone again.
// Also exits the fullscreen the rotation auto-entered, so portrait is the plain
// layout.
export function releasePixiePortraitLock() {
  if (!_portraitLocked) {
    return;
  }
  _portraitLocked = false;
  try {
    screen.orientation.unlock();
  } catch {
    /* unlock unsupported: nothing held */
  }
  if (typeof window === "undefined" || !window.__pixAutoFs) {
    return;
  }
  const exit =
    document.exitFullscreen ||
    (document as WebkitFullscreenDocument).webkitExitFullscreen;
  if (!exit) {
    return;
  }
  try {
    const p = exit.call(document);
    if (p && p.catch) {
      p.catch(function () {
        /* ignore the rejection */
      });
    }
  } catch {
    /* not fullscreen any more */
  }
}
